import { StudentNav } from '../../../common/student-nav/student-nav';
import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { NetworkService } from '../../../../services/network';
import { UserService } from '../../../../services/user.service';
import { ConversationService } from '../../../../services/conversation.service';
import { MessagingWidget } from '../../../common/messaging-widget/messaging-widget';
import { Chat } from '../../../../models/message.model';
import { UserAvatar } from '../../../common/user-avatar/user-avatar';
import { UserLink } from '../../../common/user-link/user-link';

@Component({
  selector: 'app-network-page',
  standalone: true,
  imports: [StudentNav, CommonModule, FormsModule, MessagingWidget, UserAvatar, UserLink],
  templateUrl: './network-page.html',
  styleUrls: ['./network-page.css'],
})
export class NetworkPage implements OnInit, OnDestroy {
  private networkService = inject(NetworkService);
  private userService = inject(UserService);
  private conversationService = inject(ConversationService);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  //active tab
  activeView: 'my-network' | 'grow-network' = 'my-network';

  //stores tab for persistence across reloads
  private readonly STORAGE_KEY = 'network-active-view';

  connections: any[] = [];
  allConnections: any[] = [];
  incomingRequests: any[] = [];
  sentRequests: any[] = [];
  recommended: any[] = [];

  searchQuery: string = '';

  private querySub!: Subscription;
  private initialized = false;

  loading = true;
  error = '';

  //cycles by user id so each person always gets the same color
  private readonly AVATAR_COLORS = [
    '#d4845a',
    '#5e50c4',
    '#36dc7b',
    '#1a3c5e',
    '#e79365',
    '#7968fb',
    '#42db82',
    '#3a82c9',
    '#e18655',
    '#5e52b8',
    '#ba7149',
    '#51eb91',
  ];

  ngOnInit(): void {
    const saved =
      typeof localStorage !== 'undefined' ? localStorage.getItem(this.STORAGE_KEY) : null;
    if (saved === 'my-network' || saved === 'grow-network') {
      this.activeView = saved;
    }

    this.querySub = this.route.queryParamMap.subscribe((params) => {
      const q = params.get('q') ?? '';
      if (q !== this.searchQuery) {
        this.searchQuery = q;
        if (this.initialized) {
          this.searchConnections();
        }
      }
    });

    this.loadNetworkData();
    this.loadRecommended();
    this.initialized = true;
    if (this.searchQuery) {
      this.activeView = 'my-network';
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(this.STORAGE_KEY, 'my-network');
      }
    }
  }

  ngOnDestroy(): void {
    this.querySub?.unsubscribe();
  }

  searchConnections(): void {
    this.activeView = 'my-network';
    const userId = this.userService.currentUser?.id;
    if (!userId) return;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: this.searchQuery || null },
      replaceUrl: true,
    });
    this.networkService.getConnections(userId, this.searchQuery).subscribe({
      next: (data) => {
        this.connections = data;
        this.cdr.detectChanges();
      },
      error: () => (this.error = 'Could not load connections'),
    });
  }

  loadNetworkData(): void {
    const userId = this.userService.currentUser?.id;
    if (!userId) {
      this.error = 'User not loaded';
      this.loading = false;
      return;
    }

    this.networkService.getConnections(userId, this.searchQuery).subscribe({
      next: (data) => {
        this.connections = data;
        console.log('connections:');
        console.log(this.connections);
        this.allConnections = data;
        this.cdr.detectChanges();
      },
      error: () => (this.error = 'Could not load connections'),
    });

    this.networkService.getIncomingRequests(userId).subscribe({
      next: (data) => {
        this.incomingRequests = data;
        this.cdr.detectChanges();
      },
      error: () => (this.error = 'Could not load incoming requests'),
    });

    this.networkService.getSentRequests(userId).subscribe({
      next: (data) => {
        this.sentRequests = data;
        this.cdr.detectChanges();
      },
      error: () => (this.error = 'Could not load sent requests'),
    });
  }

  private loadRecommended(): void {
    const userId = this.userService.currentUser?.id;
    if (!userId) return;

    this.networkService.getRecommended(userId).subscribe({
      next: (data) => {
        this.recommended = data;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  switchView(view: 'my-network' | 'grow-network'): void {
    this.activeView = view;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(this.STORAGE_KEY, view);
    }
    this.clearSearch();
  }

  //Filter for name and school name
  filterConnections(): void {
    const query = this.searchQuery.toLowerCase().trim();
    if (!query) {
      this.connections = [...this.allConnections];
      return;
    }
    this.connections = this.allConnections.filter((connection) => {
      const other = this.getOtherUser(connection);
      if (!other) return false;
      const fullName =
        `${other.preferredName || other.first_name} ${other.last_name}`.toLowerCase();
      const school = other.Student?.School?.name?.toLowerCase() || '';
      return fullName.includes(query) || school.includes(query);
    });
  }

  navigateView(view: 'my-network' | 'grow-network', sectionId?: string): void {
    this.switchView(view);
    if (sectionId) {
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
    }
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: null },
      replaceUrl: true,
    });
    this.loadNetworkData();
  }

  acceptRequest(connectionId: number): void {
    const userId = this.userService.currentUser?.id;
    this.networkService.respondToRequest(connectionId, 'accepted', userId!).subscribe({
      next: () => this.loadNetworkData(),
      error: () => (this.error = 'Failed to accept request'),
    });
  }

  rejectRequest(connectionId: number): void {
    const userId = this.userService.currentUser?.id;
    this.networkService.respondToRequest(connectionId, 'rejected', userId!).subscribe({
      next: () => this.loadNetworkData(),
      error: () => (this.error = 'Failed to reject request'),
    });
  }

  removeConnection(connectionId: number): void {
    const userId = this.userService.currentUser!.id;
    this.networkService.removeConnection(connectionId, userId).subscribe({
      next: () => this.loadNetworkData(),
      error: () => (this.error = 'Failed to remove connection'),
    });
  }
  pendingChat: Chat | null = null;

  openChat(connection: any): void {
    const other = this.getOtherUser(connection);
    const currentUserId = this.userService.currentUser!.id;

    this.conversationService.createDirectConversation(currentUserId, other.id).subscribe({
      next: ({ conversationId }) => {
        this.pendingChat = {
          id: conversationId,
          userId: other.id,
          name: `${other.preferredName || other.first_name} ${other.last_name}`,
          messages: [],
          lastMessage: '',
          time: '',
          unread: false,
        };
        this.cdr.detectChanges();
      },
      error: () => (this.error = 'Could not open chat'),
    });
  }
  sendRequest(addresseeId: number): void {
    const userId = this.userService.currentUser!.id;
    this.networkService.sendRequest(userId, addresseeId).subscribe({
      next: () => {
        this.recommended = this.recommended.filter((r) => r.user?.id !== addresseeId);
        this.loadNetworkData();
        this.cdr.detectChanges();
      },
      error: () => (this.error = 'Failed to send connection request'),
    });
  }

  getOtherUser(connection: any): any {
    const userId = this.userService.currentUser?.id;
    return connection.requester_id === userId ? connection.addressee : connection.requester;
  }

  isLocked(connection: any): boolean {
    return connection.locked;
  }

  getInitials(user: any): string {
    if (!user) return '?';
    return `${(user.preferredName || user.first_name)?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase();
  }

  getAvatarColor(user: any): string {
    if (!user?.id) return '#1a3c5e';
    return this.AVATAR_COLORS[user.id % this.AVATAR_COLORS.length];
  }
}
